import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  query, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  where,
  getDocs,
  limit,
  writeBatch,
  collectionGroup,
  runTransaction,
} from 'firebase/firestore';
import { db } from '../firebase';
import { canonicalDepartmentLabel } from './departmentsDb';

// ============================================
// ATTENDANCE DATA STRUCTURE
// ============================================
// Collection: "attendance"
// Structure: attendance/{userId}/{date}/
//   - email: string
//   - checkIn: timestamp (serverTimestamp)
//   - checkOut: timestamp (serverTimestamp)
//   - totalHours: number
//   - status: "IN" | "OUT" | "AUTO_CHECKOUT"
//   - dayType: "FULL_DAY" | "HALF_DAY" | "ABSENT"
//   - date: string (YYYY-MM-DD format)
//   - createdAt: timestamp
//   - updatedAt: timestamp

const FULL_DAY_HOURS = 9;

/**
 * Get today's date string in YYYY-MM-DD format
 * Uses server time for consistency
 */
const getTodayString = () => {
  return (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
};

/**
 * Robust date parser for Firestore Timestamps, ISO strings, and Date objects
 */
export const toDate = (val) => {
  if (!val) return null;
  if (val.toDate) return val.toDate();
  if (val instanceof Date) return val;
  if (typeof val === 'object' && val.seconds) return new Date(val.seconds * 1000);
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Calculate total hours between check-in and check-out
 */
const calculateTotalHours = (checkIn, checkOut) => {
  const checkInTime = toDate(checkIn);
  const checkOutTime = toDate(checkOut);
  if (!checkInTime || !checkOutTime) return 0;
  const diffMs = checkOutTime - checkInTime;
  if (diffMs < 0) return 0;
  return diffMs / (1000 * 60 * 60);
};

/**
 * Hours to show in UI when still checked in: stop at end of that record's calendar day
 * once the stored date is before "today" (same YYYY-MM-DD convention as `getTodayString`).
 * Prevents timers from growing across midnight if someone forgot to check out.
 */
export const hoursWorkedForDisplay = (checkIn, checkOut, attendanceDateStr, status) => {
  const checkInTime = toDate(checkIn);
  if (!checkInTime) return 0;
  const checkOutTime = checkOut ? toDate(checkOut) : null;
  if (checkOutTime) {
    return calculateTotalHours(checkIn, checkOut);
  }
  const st = status || (checkIn && !checkOut ? 'IN' : 'OUT');
  if (st !== 'IN') return 0;

  const parts = String(attendanceDateStr || '').split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) {
    const ms = Date.now() - checkInTime.getTime();
    return ms > 0 ? ms / (1000 * 60 * 60) : 0;
  }
  const [y, m, d] = parts;
  const endOfDayMs = Date.UTC(y, m - 1, d, 23, 59, 59, 999);
  const todayStr = getTodayString();
  const now = Date.now();
  const endMs =
    String(attendanceDateStr) < todayStr
      ? endOfDayMs
      : Math.min(now, endOfDayMs);
  const ms = endMs - checkInTime.getTime();
  return ms > 0 ? ms / (1000 * 60 * 60) : 0;
};

/**
 * Determine day type based on total hours
 */
const getDayType = (totalHours, status = '') => {
  if (status === 'IN') return '...';
  if (status === 'OUT' || status === 'AUTO_CHECKOUT') {
    if (totalHours >= FULL_DAY_HOURS) return 'FULL_DAY';
    return 'HALF_DAY';
  }
  if (totalHours > 0) {
    if (totalHours >= FULL_DAY_HOURS) return 'FULL_DAY';
    return 'HALF_DAY';
  }
  return 'ABSENT';
};

// ============================================
// EMPLOYEE ACTIONS
// ============================================

/**
 * Check In - Employee
 * Creates new attendance entry for today
 * Uses server timestamp to prevent client time manipulation
 * Prevents duplicate check-ins
 */
export const checkIn = async (email) => {
  try {
    const dateStr = getTodayString();
    const attendanceRef = doc(db, 'attendance', email, 'dates', dateStr);

    // Single round-trip: read + write atomically (faster than getDoc + setDoc)
    await runTransaction(db, async (transaction) => {
      const existingDoc = await transaction.get(attendanceRef);
      if (existingDoc.exists() && existingDoc.data().checkIn) {
        throw new Error('Already checked in today');
      }
      transaction.set(attendanceRef, {
        email,
        date: dateStr,
        checkIn: serverTimestamp(),
        checkOut: null,
        totalHours: 0,
        status: 'IN',
        dayType: '...',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });
    return { success: true, message: 'Checked in successfully' };
  } catch (error) {
    console.error('Check-in error:', error);
    throw error;
  }
};

/**
 * Check Out - Employee
 * Only allowed if check-in exists
 * Calculates total hours and updates status
 */
export const checkOut = async (email) => {
  try {
    const dateStr = getTodayString();
    const attendanceRef = doc(db, 'attendance', email, 'dates', dateStr);

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(attendanceRef);
      if (!snap.exists()) {
        throw new Error('No check-in record found for today');
      }
      const data = snap.data();
      if (!data.checkIn) {
        throw new Error('Not checked in today');
      }
      if (data.checkOut) {
        throw new Error('Already checked out today');
      }
      const checkoutDate = new Date();
      const checkInTime = toDate(data.checkIn);
      const totalHours = calculateTotalHours(checkInTime, checkoutDate);
      const dayType = getDayType(totalHours, 'OUT');
      transaction.set(attendanceRef, {
        ...data,
        checkOut: checkoutDate.toISOString(),
        status: 'OUT',
        totalHours,
        dayType,
        updatedAt: serverTimestamp(),
      });
    });

    return { success: true, message: 'Checked out successfully' };
  } catch (error) {
    console.error('Check-out error:', error);
    throw error;
  }
};

/**
 * Get today's attendance status for an employee
 */
export const getTodayAttendance = async (email) => {
  try {
    const dateStr = getTodayString();
    const attendanceRef = doc(db, 'attendance', email, 'dates', dateStr);
    const snap = await getDoc(attendanceRef);
    
    if (!snap.exists()) return null;
    
    const data = snap.data();
    
    // Calculate total hours if checked out
    let totalHours = data.totalHours || 0;
    const checkInTime = toDate(data.checkIn);
    const checkOutTime = toDate(data.checkOut);

    if (checkOutTime && checkInTime) {
      totalHours = calculateTotalHours(checkInTime, checkOutTime);
    } else if (checkInTime && data.status === 'IN') {
      totalHours = hoursWorkedForDisplay(
        data.checkIn,
        data.checkOut,
        data.date || dateStr,
        data.status
      );
    }
    
    const status = data.status || (data.checkIn && !data.checkOut ? 'IN' : 'OUT');
    
    return {
      id: snap.id,
      ...data,
      status,
      totalHours,
      dayType: getDayType(totalHours, status)
    };
  } catch (error) {
    console.error('Get today attendance error:', error);
    return null;
  }
};

// ============================================
// REAL-TIME LISTENERS
// ============================================

/**
 * Real-time listener for employee's own attendance history
 * @param {string} email - Employee email
 * @param {function} callback - Callback function with attendance data
 * @returns {function} Unsubscribe function
 */
export const onMyAttendanceChange = (email, callback) => {
  const q = query(
    collection(db, 'attendance', email, 'dates'),
    orderBy('date', 'desc'),
    limit(120)
  );

  return onSnapshot(q, (snapshot) => {
    const data = [];
    snapshot.forEach((docSnapshot) => {
      const docData = docSnapshot.data();
      
      // Format timestamps for display
      const checkInTime = toDate(docData.checkIn);
      const checkOutTime = toDate(docData.checkOut);
      
      let totalHours = docData.totalHours || 0;
      const status = docData.status || (checkInTime && !checkOutTime ? 'IN' : 'OUT');
      if (checkInTime && checkOutTime) {
        totalHours = calculateTotalHours(checkInTime, checkOutTime);
      } else if (checkInTime && status === 'IN') {
        totalHours = hoursWorkedForDisplay(
          docData.checkIn,
          docData.checkOut,
          docData.date,
          status
        );
      }

      
      data.push({
        id: docSnapshot.id,
        ...docData,
        status,
        totalHours,
        dayType: getDayType(totalHours, status),
        // Format timestamps for display
        checkInFormatted: checkInTime ? checkInTime.toLocaleString() : '--',
        checkOutFormatted: checkOutTime ? checkOutTime.toLocaleString() : '--'
      });
    });
    callback(data);
  }, (error) => {
    console.error('My attendance listener error:', error);
  });
};

// One collectionGroup listener per date; multiple React subscribers share callbacks (Strict Mode safe).
const attendanceListeners = new Map();

/** Cached EMPLOYEE users (doc id → data). Refreshed on TTL to avoid N reads on every attendance tick. */
let cachedEmployeeUsersMap = null;
let cachedEmployeeUsersFetchedAt = 0;
const EMPLOYEE_USERS_CACHE_TTL_MS = 120000;

async function getCachedEmployeeUsers() {
  const now = Date.now();
  if (
    cachedEmployeeUsersMap &&
    now - cachedEmployeeUsersFetchedAt < EMPLOYEE_USERS_CACHE_TTL_MS
  ) {
    return cachedEmployeeUsersMap;
  }
  const usersSnap = await getDocs(
    query(collection(db, 'users'), where('role', '==', 'EMPLOYEE'))
  );
  const users = {};
  usersSnap.forEach((u) => {
    users[u.id] = u.data();
  });
  cachedEmployeeUsersMap = { users, size: usersSnap.size };
  cachedEmployeeUsersFetchedAt = now;
  return cachedEmployeeUsersMap;
}

/** Call after HR approves/changes a user so absentee list stays correct without waiting for TTL. */
export const invalidateEmployeeUsersCache = () => {
  cachedEmployeeUsersMap = null;
  cachedEmployeeUsersFetchedAt = 0;
};

/** Warm users cache before / alongside attendance snapshots so full HR merge is not blocked on cold getDocs. */
export const prefetchAttendanceEmployeeCache = () => getCachedEmployeeUsers();

/** Mimic QuerySnapshot.forEach for payloads built from parallel getDoc calls. */
function snapshotLikeFromDocSnapshots(docSnapshots) {
  const existing = docSnapshots.filter((s) => s.exists());
  return {
    forEach(fn) {
      existing.forEach(fn);
    },
  };
}

/**
 * HR attendance without collectionGroup (works when prod index is missing or group query fails).
 * One getDoc per employee for `attendance/{email}/dates/{dateStr}`.
 */
async function fetchAttendanceSnapshotViaEmployeeDocs(dateStr) {
  const { users } = await getCachedEmployeeUsers();
  const emails = Object.keys(users);
  const snaps = [];
  const CHUNK = 40;
  for (let i = 0; i < emails.length; i += CHUNK) {
    const slice = emails.slice(i, i + CHUNK);
    const batch = await Promise.all(
      slice.map((email) => getDoc(doc(db, 'attendance', email, 'dates', dateStr)))
    );
    snaps.push(...batch);
  }
  return snapshotLikeFromDocSnapshots(snaps);
}

async function notifyAttendanceFromEmployeePathFetch(dateStr, entry, notifyAll) {
  const pseudoSnap = await fetchAttendanceSnapshotViaEmployeeDocs(dateStr);
  const partialPayload = buildPartialAttendancePayload(pseudoSnap, dateStr);
  entry.lastPayload = partialPayload;
  notifyAll(partialPayload);
  const payload = await buildAttendancePayloadFromSnapshot(pseudoSnap, dateStr);
  entry.lastPayload = payload;
  notifyAll(payload);
}

function buildPartialAttendancePayload(attendanceSnapshot, dateStr) {
  const attendanceRecords = [];
  let present = 0;
  let late = 0;

  const [year, month, day] = dateStr.split('-').map(Number);
  const lateThreshold = new Date(year, month - 1, day, 9, 30, 0);

  attendanceSnapshot.forEach((docSnap) => {
    const data = docSnap.data();
    const email = data.email;
    const status = data.status || (data.checkIn && !data.checkOut ? 'IN' : 'OUT');

    const checkInTime = toDate(data.checkIn);
    const checkOutTime = toDate(data.checkOut);

    let totalHours = data.totalHours || 0;
    if (checkInTime) {
      present++;
      if (checkInTime > lateThreshold) late++;
      if (checkOutTime) {
        totalHours = calculateTotalHours(checkInTime, checkOutTime);
      } else if (status === 'IN') {
        totalHours = hoursWorkedForDisplay(
          data.checkIn,
          data.checkOut,
          data.date || dateStr,
          status
        );
      }
    }

    attendanceRecords.push({
      id: docSnap.id,
      userId: email,
      userName: email.split('@')[0],
      department: 'N/A',
      ...data,
      status,
      totalHours,
      dayType: getDayType(totalHours, status),
    });
  });

  return {
    records: attendanceRecords,
    summary: {
      totalEmployees: attendanceRecords.length,
      present,
      absent: 0,
      late,
      attendanceRate: present > 0 ? 100 : 0,
    },
  };
}

/**
 * Shared aggregation for HR daily attendance (used by snapshot listener and getDocs fallback).
 */
async function buildAttendancePayloadFromSnapshot(attendanceSnapshot, dateStr) {
  const { users, size: totalEmployees } = await getCachedEmployeeUsers();

  const attendanceRecords = [];
  const presentUserEmails = new Set();
  let present = 0;
  let late = 0;

  const [year, month, day] = dateStr.split('-').map(Number);
  const lateThreshold = new Date(year, month - 1, day, 9, 30, 0);

  attendanceSnapshot.forEach((docSnap) => {
    const data = docSnap.data();
    const email = data.email;
    const user = users[email] || {};
    const status = data.status || (data.checkIn && !data.checkOut ? 'IN' : 'OUT');

    const checkInTime = toDate(data.checkIn);
    const checkOutTime = toDate(data.checkOut);

    let totalHours = data.totalHours || 0;
    if (checkInTime) {
      present++;
      const rowDate = data.date || dateStr;
      if (checkInTime > lateThreshold) late++;
      if (checkOutTime) {
        totalHours = calculateTotalHours(checkInTime, checkOutTime);
      } else if (status === 'IN') {
        totalHours = hoursWorkedForDisplay(
          data.checkIn,
          data.checkOut,
          rowDate,
          status
        );
      }
    }

    presentUserEmails.add(email);
    attendanceRecords.push({
      ...data,
      id: docSnap.id,
      userId: email,
      userName: user.name || email.split('@')[0],
      department: canonicalDepartmentLabel(user.department),
      status,
      totalHours,
      dayType: getDayType(totalHours, status),
    });
  });

  Object.keys(users).forEach((email) => {
    if (!presentUserEmails.has(email)) {
      attendanceRecords.push({
        id: `${email}_${dateStr}`,
        userId: email,
        userName: users[email].name || email.split('@')[0],
        department: canonicalDepartmentLabel(users[email].department),
        email,
        date: dateStr,
        status: 'ABSENT',
        dayType: 'ABSENT',
        checkIn: null,
        checkOut: null,
        totalHours: 0,
      });
    }
  });

  return {
    records: attendanceRecords,
    summary: {
      totalEmployees,
      present,
      absent: Math.max(0, totalEmployees - present),
      late,
      attendanceRate:
        totalEmployees > 0 ? Math.round((present / totalEmployees) * 100) : 0,
    },
  };
}

/**
 * Real-time listener for HR - All employees' attendance + Summary
 * Exactly one onSnapshot per date; extra subscribers only add callbacks (avoids Firestore SDK crashes).
 */
export const onAllAttendanceChange = (dateStr = getTodayString(), callback) => {
  const listenerKey = `all_${dateStr}`;

  const notifyAll = (payload) => {
    const entry = attendanceListeners.get(listenerKey);
    if (!entry?.callbacks) return;
    entry.callbacks.forEach((cb) => {
      try {
        cb(payload);
      } catch (e) {
        console.error('Attendance callback error:', e);
      }
    });
  };

  if (attendanceListeners.has(listenerKey)) {
    const entry = attendanceListeners.get(listenerKey);
    entry.callbacks.add(callback);
    if (entry.lastPayload) {
      callback(entry.lastPayload);
    }
    return () => {
      entry.callbacks.delete(callback);
      if (entry.callbacks.size === 0) {
        if (entry.unsub) entry.unsub();
        attendanceListeners.delete(listenerKey);
      }
    };
  }

  const callbacks = new Set([callback]);
  const entry = { callbacks, unsub: null, lastPayload: null, pollTimer: null };
  attendanceListeners.set(listenerKey, entry);

  void getCachedEmployeeUsers();

  const attendanceQuery = query(
    collectionGroup(db, 'dates'),
    where('date', '==', dateStr)
  );

  const startEmployeePathPolling = () => {
    if (entry.pollTimer != null) return;
    entry.pollTimer = setInterval(() => {
      void (async () => {
        try {
          await notifyAttendanceFromEmployeePathFetch(dateStr, entry, notifyAll);
        } catch (e) {
          console.error('Attendance poll refresh failed:', e);
        }
      })();
    }, 35000);
  };

  const onCollectionGroupError = (error) => {
    console.error('Attendance collectionGroup error:', error);
    if (entry.unsub) {
      entry.unsub();
      entry.unsub = null;
    }
    void (async () => {
      try {
        await notifyAttendanceFromEmployeePathFetch(dateStr, entry, notifyAll);
        startEmployeePathPolling();
      } catch (e2) {
        console.error('Attendance employee-path fallback failed:', e2);
      }
    })();
  };

  entry.unsub = onSnapshot(
    attendanceQuery,
    async (attendanceSnapshot) => {
      try {
        const partialPayload = buildPartialAttendancePayload(attendanceSnapshot, dateStr);
        entry.lastPayload = partialPayload;
        notifyAll(partialPayload);

        const payload = await buildAttendancePayloadFromSnapshot(
          attendanceSnapshot,
          dateStr
        );
        entry.lastPayload = payload;
        notifyAll(payload);
      } catch (e) {
        console.error('Attendance processing error:', e);
        try {
          await notifyAttendanceFromEmployeePathFetch(dateStr, entry, notifyAll);
        } catch (e2) {
          console.error('Attendance merge fallback (employee paths) failed:', e2);
        }
      }
    },
    onCollectionGroupError
  );

  return () => {
    entry.callbacks.delete(callback);
    if (entry.callbacks.size === 0) {
      if (entry.unsub) entry.unsub();
      if (entry.pollTimer != null) {
        clearInterval(entry.pollTimer);
        entry.pollTimer = null;
      }
      attendanceListeners.delete(listenerKey);
    }
  };
};

// Deprecated in favor of combined onAllAttendanceChange
export const onAttendanceSummaryChange = (dateStr = getTodayString(), callback) => {
  return onAllAttendanceChange(dateStr, (data) => callback(data.summary));
};

/**
 * Get all attendance records for a specific date range (for reports)
 */
export const fetchAttendanceByDateRange = async (startDate, endDate, userEmail = null) => {
  try {
    // If specific user, query their attendance
    if (userEmail) {
      const q = query(
        collection(db, 'attendance', userEmail, 'dates'),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const data = [];
      snapshot.forEach((docSnapshot) => {
        const docData = docSnapshot.data();
        const status = docData.status || (docData.checkIn && !docData.checkOut ? 'IN' : 'OUT');
        let totalHours = docData.totalHours || 0;
        if (docData.checkOut && docData.checkIn) {
          totalHours = calculateTotalHours(docData.checkIn, docData.checkOut);
        } else if (docData.checkIn && status === 'IN') {
          totalHours = hoursWorkedForDisplay(
            docData.checkIn,
            docData.checkOut,
            docData.date,
            status
          );
        }
        data.push({
          id: docSnapshot.id,
          ...docData,
          status,
          totalHours,
          dayType: getDayType(totalHours, status)
        });
      });
      return data;
    }
    
    // For all users, we'll need to aggregate from each user
    // This is a simplified version - in production, consider using Cloud Functions
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const allAttendance = [];
    
    for (const userDoc of usersSnapshot.docs) {
      if (userDoc.data().role === 'HR') continue;
      
      const userEmail = userDoc.id;
      const q = query(
        collection(db, 'attendance', userEmail, 'dates'),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc')
      );
      
      const snapshot = await getDocs(q);
      snapshot.forEach((docSnapshot) => {
        const docData = docSnapshot.data();
        const status = docData.status || (docData.checkIn && !docData.checkOut ? 'IN' : 'OUT');
        let totalHours = docData.totalHours || 0;
        if (docData.checkOut && docData.checkIn) {
          totalHours = calculateTotalHours(docData.checkIn, docData.checkOut);
        } else if (docData.checkIn && status === 'IN') {
          totalHours = hoursWorkedForDisplay(
            docData.checkIn,
            docData.checkOut,
            docData.date,
            status
          );
        }
        allAttendance.push({
          ...docData,
          id: docSnapshot.id,
          userId: userEmail,
          userName: userDoc.data().name || userEmail.split('@')[0],
          department: canonicalDepartmentLabel(userDoc.data().department),
          status,
          totalHours,
          dayType: getDayType(totalHours, status)
        });
      });
    }
    
    return allAttendance.sort((a, b) => b.date.localeCompare(a.date));
  } catch (error) {
    console.error('Fetch attendance by date range error:', error);
    throw error;
  }
};

/**
 * HR dashboard trend: "present" count per calendar day — ONE collectionGroup query (fast).
 * Present = has check-in or status IN / OUT / AUTO_CHECKOUT (same rules as dashboard chart).
 * If the query fails (e.g. missing index), falls back to fetchAttendanceByDateRange (slow).
 */
export const fetchDailyPresentCountsForRange = async (startDate, endDate) => {
  const aggregateFromRows = (rows) => {
    const countsByDate = {};
    rows.forEach((r) => {
      if (!r.date) return;
      if (
        r.checkIn ||
        r.status === 'IN' ||
        r.status === 'OUT' ||
        r.status === 'AUTO_CHECKOUT'
      ) {
        countsByDate[r.date] = (countsByDate[r.date] || 0) + 1;
      }
    });
    return countsByDate;
  };

  try {
    const q = query(
      collectionGroup(db, 'dates'),
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );
    const snapshot = await getDocs(q);
    const countsByDate = {};
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      const ds = data.date;
      if (!ds) return;
      if (
        data.checkIn ||
        data.status === 'IN' ||
        data.status === 'OUT' ||
        data.status === 'AUTO_CHECKOUT'
      ) {
        countsByDate[ds] = (countsByDate[ds] || 0) + 1;
      }
    });
    return countsByDate;
  } catch (e) {
    console.warn(
      'fetchDailyPresentCountsForRange: using slow fallback (create Firestore index if needed)',
      e
    );
    const rows = await fetchAttendanceByDateRange(startDate, endDate, null);
    return aggregateFromRows(rows);
  }
};

/**
 * Fast HR month/range fetch using a single collectionGroup query.
 * Returns only stored attendance rows; callers can derive absences client-side.
 */
export const fetchAttendanceRowsForRangeFast = async (startDate, endDate) => {
  try {
    const { users } = await getCachedEmployeeUsers();
    const q = query(
      collectionGroup(db, 'dates'),
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );
    const snapshot = await getDocs(q);
    const rows = [];

    snapshot.forEach((docSnapshot) => {
      const docData = docSnapshot.data();
      const email = docData.email;
      const user = users[email] || {};
      const status = docData.status || (docData.checkIn && !docData.checkOut ? 'IN' : 'OUT');
      let totalHours = docData.totalHours || 0;
      if (docData.checkOut && docData.checkIn) {
        totalHours = calculateTotalHours(docData.checkIn, docData.checkOut);
      } else if (docData.checkIn && status === 'IN') {
        totalHours = hoursWorkedForDisplay(
          docData.checkIn,
          docData.checkOut,
          docData.date,
          status
        );
      }

      rows.push({
        ...docData,
        id: docSnapshot.id,
        userId: email,
        userName: user.name || email?.split('@')[0] || 'Unknown',
        department: canonicalDepartmentLabel(user.department),
        status,
        totalHours,
        dayType: getDayType(totalHours, status),
      });
    });

    return rows.sort((a, b) => {
      const dateCompare = String(b.date || '').localeCompare(String(a.date || ''));
      if (dateCompare !== 0) return dateCompare;
      return String(a.userName || '').localeCompare(String(b.userName || ''));
    });
  } catch (error) {
    console.warn('fetchAttendanceRowsForRangeFast fallback:', error);
    return fetchAttendanceByDateRange(startDate, endDate, null);
  }
};

// ============================================
// AUTO CHECKOUT (Cloud Function Simulation)
// ============================================

/**
 * Auto-checkout users who forgot to check out
 * This would typically be done via a Cloud Function scheduled daily
 * This client-side version is for manual trigger or testing
 */
export const autoCheckoutForgotten = async (dateStr = getTodayString()) => {
  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const batch = writeBatch(db);
    let count = 0;
    
    for (const userDoc of usersSnapshot.docs) {
      if (userDoc.data().role === 'HR') continue;
      
      const userEmail = userDoc.id;
      const attendanceRef = doc(db, 'attendance', userEmail, 'dates', dateStr);
      const snap = await getDoc(attendanceRef);
      
      if (snap.exists()) {
        const data = snap.data();
        // If checked in but not checked out
        const currentStatus = data.status || (data.checkIn && !data.checkOut ? 'IN' : 'OUT');
        if (data.checkIn && !data.checkOut && currentStatus === 'IN') {
          // Set checkout to end of day (11:59 PM)
          const [year, month, day] = dateStr.split('-').map(Number);
          const endOfDay = new Date(year, month - 1, day, 23, 59, 59);
          
          const totalHours = calculateTotalHours(data.checkIn, endOfDay);
          
          batch.set(attendanceRef, {
            ...data,
            checkOut: endOfDay.toISOString(),
            status: 'AUTO_CHECKOUT',
            totalHours,
            dayType: getDayType(totalHours, 'AUTO_CHECKOUT'),
            updatedAt: serverTimestamp()
          });
          count++;
        }
      }
    }
    
    if (count > 0) {
      await batch.commit();
    }
    
    return { success: true, message: `${count} users auto-checked out` };
  } catch (error) {
    console.error('Auto-checkout error:', error);
    throw error;
  }
};

// ============================================
// HR ADMIN FUNCTIONS
// ============================================

/**
 * Get attendance summary for HR dashboard
 */

/**
 * Export attendance data to CSV format
 */
export const exportAttendanceToCSV = async (startDate, endDate) => {
  try {
    const data = await fetchAttendanceByDateRange(startDate, endDate);
    
    // CSV Headers
    const headers = ['Date', 'Email', 'Name', 'Department', 'Check In', 'Check Out', 'Total Hours', 'Status'];
    
    // CSV Rows
    const rows = data.map(record => [
      record.date,
      record.email,
      record.userName || record.email,
      record.department || 'N/A',
      record.checkInFormatted || '--',
      record.checkOutFormatted || '--',
      record.totalHours || 0,
      record.status
    ]);
    
    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    return csvContent;
  } catch (error) {
    console.error('Export attendance error:', error);
    throw error;
  }
};

// ============================================
// LEGACY SUPPORT
// ============================================

/**
 * Legacy fetch function for backward compatibility
 * @deprecated Use onMyAttendanceChange for real-time updates
 */
export const fetchMyAttendance = async (email) => {
  try {
    const q = query(
      collection(db, 'attendance', email, 'dates'), 
      orderBy('date', 'desc'),
      limit(30)
    );
    
    const snapshot = await getDocs(q);
    const data = [];
    snapshot.forEach((docSnapshot) => {
      const docData = docSnapshot.data();
      const status = docData.status || (docData.checkIn && !docData.checkOut ? 'IN' : 'OUT');
      let totalHours = docData.totalHours || 0;
      if (docData.checkOut && docData.checkIn) {
        totalHours = calculateTotalHours(docData.checkIn, docData.checkOut);
      } else if (docData.checkIn && status === 'IN') {
        totalHours = hoursWorkedForDisplay(
          docData.checkIn,
          docData.checkOut,
          docData.date,
          status
        );
      }
      data.push({
        id: docSnapshot.id,
        ...docData,
        status,
        totalHours,
        dayType: getDayType(totalHours, status)
      });
    });
    return data;
  } catch (error) {
    console.error('Fetch my attendance error:', error);
    return [];
  }
};

